import Dexie, { Table } from 'dexie';
import { WorkItem, AppSettings, WorkItemStatus } from '../types';
import LZString from 'lz-string';
import { errorHandler } from '../utils/errorHandling';

export class CleanRoomDatabase extends Dexie {
  workItems!: Table<WorkItem>;
  settings!: Table<AppSettings & { id: string }>;

  constructor() {
    super('CleanRoomDB');
    
    this.version(1).stores({
      workItems: 'id, level1, level2, *tags, metadata.modified',
      settings: 'id'
    });
    
    // Add error handlers
    this.on('ready', async () => {
      console.log('Database ready');
      // Run migration for existing data
      await this.migrateProgressToStatus();
    });
  }

  async migrateProgressToStatus(): Promise<void> {
    try {
      const items = await this.workItems.toArray();
      let migratedCount = 0;

      for (const item of items) {
        // Check if item has old progress field but no status field
        if ('progress' in item && !item.status) {
          let status: WorkItemStatus = 'Backlog';

          // Convert progress percentage to status
          if ('showProgress' in item && item.showProgress) {
            const progress = (item as any).progress || 0;
            if (progress === 0) {
              status = 'Backlog';
            } else if (progress > 0 && progress < 100) {
              status = 'In Progress';
            } else if (progress === 100) {
              status = 'Done';
            }
          }

          // Update item with new status field
          item.status = status;

          // Remove old progress fields
          delete (item as any).progress;
          delete (item as any).showProgress;

          await this.workItems.put(item);
          migratedCount++;
        }
      }

      if (migratedCount > 0) {
        console.log(`Migrated ${migratedCount} items from progress to status`);
      }
    } catch (error) {
      console.error('Migration failed:', error);
      // Don't throw - allow app to continue even if migration fails
    }
  }

  async saveWorkItem(item: WorkItem): Promise<void> {
    try {
      // Validate item before saving
      this.validateWorkItem(item);
      
      item.metadata.modified = new Date();
      await this.workItems.put(item);
    } catch (error) {
      errorHandler.handle(error, 'error', () => this.saveWorkItem(item));
      throw new Error(`Failed to save item: ${this.extractErrorMessage(error)}`);
    }
  }

  async deleteWorkItem(id: string): Promise<void> {
    try {
      const exists = await this.workItems.get(id);
      if (!exists) {
        throw new Error('Item not found');
      }
      await this.workItems.delete(id);
    } catch (error) {
      errorHandler.handle(error, 'error', () => this.deleteWorkItem(id));
      throw new Error(`Failed to delete item: ${this.extractErrorMessage(error)}`);
    }
  }

  async getAllWorkItems(): Promise<WorkItem[]> {
    try {
      return await this.workItems.toArray();
    } catch (error) {
      errorHandler.handle(error, 'error', () => this.getAllWorkItems());
      console.error('Failed to get work items:', error);
      return [];
    }
  }

  async searchWorkItems(searchTerm: string): Promise<WorkItem[]> {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        return [];
      }
      
      const lowerSearch = searchTerm.toLowerCase();
      return await this.workItems
        .filter(item => 
          item.fileName.toLowerCase().includes(lowerSearch) ||
          item.description.toLowerCase().includes(lowerSearch) ||
          item.level1.toLowerCase().includes(lowerSearch) ||
          item.level2.toLowerCase().includes(lowerSearch) ||
          item.tags.some(tag => tag.toLowerCase().includes(lowerSearch))
        )
        .toArray();
    } catch (error) {
      errorHandler.handle(error, 'warning');
      return [];
    }
  }

  async exportToJSON(): Promise<string> {
    try {
      const items = await this.getAllWorkItems();
      const settings = await this.settings.toArray();
      
      if (items.length === 0) {
        throw new Error('No data to export');
      }
      
      const exportData = {
        version: '1.0.0',
        exportDate: new Date(),
        items,
        settings: settings[0] || null,
        metadata: {
          itemCount: items.length,
          exportedBy: 'Focus Hub',
          browserInfo: navigator.userAgent
        }
      };
      
      const compressed = LZString.compressToUTF16(JSON.stringify(exportData));
      if (!compressed) {
        throw new Error('Failed to compress data');
      }
      
      return compressed;
    } catch (error) {
      errorHandler.handle(error, 'error');
      throw new Error(`Export failed: ${this.extractErrorMessage(error)}`);
    }
  }

  async importFromJSON(compressedData: string): Promise<void> {
    try {
      if (!compressedData) {
        throw new Error('No data provided');
      }
      
      const jsonString = LZString.decompressFromUTF16(compressedData);
      if (!jsonString) {
        throw new Error('Invalid data format - decompression failed');
      }
      
      let data: any;
      try {
        data = JSON.parse(jsonString);
      } catch {
        throw new Error('Invalid JSON format');
      }
      
      // Validate import data structure
      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Invalid data structure - missing items');
      }
      
      if (data.version !== '1.0.0') {
        throw new Error(`Unsupported version: ${data.version}`);
      }
      
      // Backup current data before import
      const backupData = await this.getAllWorkItems();
      
      try {
        await this.transaction('rw', this.workItems, this.settings, async () => {
          // Clear existing data if requested
          const shouldClear = window.confirm('Replace existing data? Click Cancel to merge.');
          if (shouldClear) {
            await this.workItems.clear();
          }
          
          // Import items with validation
          let skippedCount = 0;

          for (const item of data.items) {
            try {
              // Restore dates
              item.metadata.modified = new Date(item.metadata.modified);
              item.metadata.created = new Date(item.metadata.created);
              item.comments.forEach((comment: any) => {
                comment.timestamp = new Date(comment.timestamp);
              });

              // Validate before importing
              this.validateWorkItem(item);

              await this.workItems.put(item);
            } catch (itemError) {
              console.warn(`Skipped invalid item: ${item.fileName}`, itemError);
              skippedCount++;
            }
          }
          
          if (data.settings) {
            await this.settings.put({ ...data.settings, id: 'main' });
          }
          
          if (skippedCount > 0) {
            errorHandler.handle(
              `Import completed with warnings: ${skippedCount} items skipped`,
              'warning'
            );
          }
        });
      } catch (transactionError) {
        // Restore backup on failure
        console.error('Import transaction failed, restoring backup');
        await this.workItems.clear();
        for (const item of backupData) {
          await this.workItems.put(item);
        }
        throw transactionError;
      }
    } catch (error) {
      errorHandler.handle(error, 'error');
      throw new Error(`Import failed: ${this.extractErrorMessage(error)}`);
    }
  }

  async getStorageEstimate(): Promise<{ usage: number; quota: number; percentage: number }> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const percentage = quota > 0 ? (usage / quota) * 100 : 0;
        
        // Warn if storage is getting full
        if (percentage > 80) {
          errorHandler.handle(
            `Storage usage high: ${percentage.toFixed(1)}%`,
            'warning'
          );
        }
        
        return { usage, quota, percentage };
      }
    } catch (error) {
      console.warn('Storage estimate failed:', error);
    }
    
    return { usage: 0, quota: 0, percentage: 0 };
  }
  
  private validateWorkItem(item: WorkItem): void {
    const errors: string[] = [];
    
    if (!item.id) errors.push('Item ID is required');
    if (!item.fileName || item.fileName.trim().length < 1) {
      errors.push('File name is required');
    }
    if (item.fileName && item.fileName.length > 255) {
      errors.push('File name is too long (max 255 characters)');
    }
    if (!item.level1) errors.push('Category is required');
    if (!item.metadata) errors.push('Metadata is required');
    if (!Array.isArray(item.tags)) errors.push('Tags must be an array');
    if (!Array.isArray(item.comments)) errors.push('Comments must be an array');
    
    // Validate tags
    if (item.tags.length > 20) {
      errors.push('Too many tags (max 20)');
    }
    item.tags.forEach((tag, index) => {
      if (typeof tag !== 'string') {
        errors.push(`Tag at index ${index} must be a string`);
      }
      if (tag.length > 50) {
        errors.push(`Tag "${tag}" is too long (max 50 characters)`);
      }
    });
    
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }
  }
  
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.name === 'QuotaExceededError') {
      return 'Storage quota exceeded. Please delete some items.';
    }
    if (error?.name === 'ConstraintError') {
      return 'Database constraint violation. Item may already exist.';
    }
    return 'Unknown error occurred';
  }
  
  async checkHealth(): Promise<boolean> {
    try {
      await this.workItems.count();
      return true;
    } catch {
      return false;
    }
  }
}

export const db = new CleanRoomDatabase();